/**
 * Adapted from PlayCanvas Engine's gsplat-renderer.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type * as THREE from 'three'
import { GSPLAT_INSTANCE_SIZE } from '@/plugins/gsplat/core/gsplatBatching'
import type { GSplatMaterialTemplate } from '@/plugins/gsplat/effects/GSplatMaterialTemplate'
import type {
  GSplatBufferLeaf,
  GSplatDrawObjectLeaf,
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type { Layer } from '@/plugins/gsplat/lod/Layer'
import { getLoggerManager } from '@/shared/logging'
import { FisheyeProjection } from './FisheyeProjection'
import type { GSplatRenderPassMask } from './GSplatPipelineTypes'
import type { GSplatVaryings } from './GSplatVaryings'

export {
  GSPLAT_RENDER_MODE_FORWARD,
  GSPLAT_RENDER_MODE_NONE,
  GSPLAT_RENDER_MODE_SHADOW,
  type GSplatRenderPassMask,
} from './GSplatPipelineTypes'

export { GSPLAT_INSTANCE_SIZE }

export type GSplatOrderResource = GSplatTextureLeaf | GSplatBufferLeaf

/** Renderer 从共享 WorkBuffer 借用的最小、backend-neutral 数据源。 */
export interface GSplatRendererDataSource {
  readonly format: GSplatFormat
  readonly textureSize: number
  readonly orderResource: GSplatOrderResource | null
  getTexture(name: string): GSplatTextureLeaf | undefined
  syncWithFormat(): void
}

export interface GSplatRenderViewParams {
  readonly cameraNode: THREE.Object3D
  readonly radialSorting: boolean
  readonly alphaClip: number
  readonly alphaClipForward: number
  readonly minPixelSize: number
  readonly minContribution: number
  readonly foveationStrength: number
  readonly foveationCenter: number
  readonly antiAlias: boolean
  readonly fisheye: number
  readonly material: GSplatMaterialLeaf
  readonly materialRevision: number
  /** 当前共享 shader effect 是否属于这个 View 中的 Placement。 */
  readonly shaderEffectEnabled?: boolean
  /** backend-neutral logical material，Prepare 时同步到当前 View/Manager leaf。 */
  readonly materialTemplate?: GSplatMaterialTemplate
  readonly colorRamp: unknown | null
  readonly colorRampIntensity: number
  readonly useFog: boolean
  readonly varyings: Pick<GSplatVaryings, 'words'>
  readonly width?: number
  readonly height?: number
  readonly pixelRatio?: number
  /** 当前 View 使用的世界坐标 RTE 原点；null/undefined 表示世界原点。 */
  readonly sceneOrigin?: THREE.Vector3 | null
}

export type GSplatRendererWorld = object

export interface GSplatRendererWorldState {
  readonly totalActiveSplats: number
}

export interface GSplatRendererOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly node: THREE.Object3D
  readonly cameraNode: THREE.Object3D
  readonly layer: Layer<object, GSplatDrawObjectLeaf>
  readonly workBuffer: GSplatRendererDataSource
}

/**
 * Unified renderer 的共同基类。
 *
 * 与冻结 PlayCanvas 一致，Manager 后续只通过这个基类调用公共 lifecycle/hooks；backend 差异留在
 * adapter leaf，不能再把 Quad/Hybrid 的公共状态复制回 Manager。
 */
export class GSplatRenderer {
  readonly adapter: GSplatGraphicsAdapter
  readonly node: THREE.Object3D
  readonly cameraNode: THREE.Object3D
  readonly layer: Layer<object, GSplatDrawObjectLeaf>
  workBuffer: GSplatRendererDataSource
  renderMode: GSplatRenderPassMask | undefined
  readonly fisheyeProj = new FisheyeProjection()

  protected workBufferFormatVersion: number
  protected destroyed = false

  constructor(options: GSplatRendererOptions) {
    this.adapter = options.adapter
    this.node = options.node
    this.cameraNode = options.cameraNode
    this.layer = options.layer
    this.workBuffer = options.workBuffer
    this.workBufferFormatVersion = options.workBuffer.format.extraStreamsVersion
  }

  destroy(): void {
    this.destroyed = true
  }

  resolveFisheye(fisheye: number): number {
    const cameraState = this.cameraNode as THREE.Object3D & {
      readonly userData?: Readonly<{ xrActive?: boolean }>
      readonly xrActive?: boolean
    }
    const xrActive = cameraState.xrActive === true || cameraState.userData?.xrActive === true
    if (xrActive && fisheye > 0) {
      warnFisheyeXrOnce()
      return 0
    }
    return fisheye
  }

  setRenderMode(renderMode: GSplatRenderPassMask): void {
    this.assertActive()
    this.renderMode = renderMode
  }

  get usesGpuSort(): boolean {
    return false
  }

  get requiresBounds(): boolean {
    return false
  }

  get requiresCpuSort(): boolean {
    return !this.usesGpuSort
  }

  get material(): GSplatMaterialLeaf | null {
    return null
  }

  setDataSource(source: GSplatRendererDataSource): void {
    this.assertActive()
    this.workBuffer = source
    this.onWorkBufferFormatChanged()
  }

  onWorkBufferFormatChanged(): void {}

  update(_count: number, _textureSize: number): void {}

  /**
   * WorkBuffer replacement transaction 回滚后，重新绑定当前 data source 的 GPU leaves。
   *
   * 该 hook 不得改变 visible count、draw mode 或材质配置；它只恢复旧 render-ready
   * storage 的引用，使失败帧之后仍可继续绘制上一版状态。
   */
  refreshDataSourceBindings(): void {}

  setGpuSortedRendering(
    _drawSlot: number,
    _sortedIds: GSplatBufferLeaf,
    _numSplatsBuffer: GSplatBufferLeaf,
    _textureSize: number
  ): void {}

  setCpuSortedRendering(): void {}

  setOrderData(): void {}

  frameUpdate(_params: GSplatRenderViewParams, _exposure?: number, _fogParams?: unknown): void {}

  updateOverdrawMode(_params: GSplatRenderViewParams): void {}

  invalidateCullUpload(): void {}

  setAggregateAabb(aabb: THREE.Box3 | null): void {
    this.assertActive()
    this.setRasterLeafBounds(aabb)
  }

  prepareRenderView(
    _world: GSplatRendererWorld,
    _worldState: GSplatRendererWorldState,
    _params: GSplatRenderViewParams
  ): boolean {
    return false
  }

  preparePickingView(
    _world: GSplatRendererWorld,
    _worldState: GSplatRendererWorldState,
    _pickParams: GSplatRenderViewParams
  ): GSplatDrawObjectLeaf | null {
    return null
  }

  protected assertActive(): void {
    if (this.destroyed) throw new Error(`${this.constructor.name} 已销毁`)
  }

  protected setRasterLeafBounds(_aabb: THREE.Box3 | null): void {}
}

const log = getLoggerManager().getLogger('gsplat:renderer')
let warnedFisheyeXr = false

function warnFisheyeXrOnce(): void {
  if (warnedFisheyeXr) return
  warnedFisheyeXr = true
  log.warn('GSplat 鱼眼投影不支持 XR，已自动禁用')
}
